import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
// @ts-ignore
import { get_domain } from './urls';

const READY_SELECTOR = '#personalised-pricing';
const POLL_INTERVAL_MS = 300;
const READY_TIMEOUT_MS = 20000;

const waitForIframeContent = (iframe: HTMLIFrameElement): Promise<HTMLElement> =>
  new Promise((resolve, reject) => {
    const start = Date.now();

    const check = () => {
      const doc = iframe.contentDocument;
      const target = doc?.querySelector<HTMLElement>(READY_SELECTOR);

      if (target) {
        resolve(target);
        return;
      }

      if (Date.now() - start > READY_TIMEOUT_MS) {
        reject(new Error('Timed out waiting for the personalised plan page to load.'));
        return;
      }

      setTimeout(check, POLL_INTERVAL_MS);
    };

    iframe.addEventListener('load', check, { once: true });
  });

// Page slices are rasterised as PNG for downloads (existing behaviour) or JPEG
// for uploads/sharing — the pages are flattened onto a white background anyway,
// and JPEG keeps the file well under the CDN upload endpoint's 5MB cap.
type PdfImageFormat = 'PNG' | 'JPEG';

// buildPersonalisedPlanPdf renders the student's personalised-plan page in a
// hidden iframe and assembles the paginated jsPDF document — shared by the
// "Download PDF" (save to disk) and "Share" (upload the Blob to the CDN) flows.
const buildPersonalisedPlanPdf = async (
  studentUuid: string,
  imageFormat: PdfImageFormat = 'PNG'
): Promise<jsPDF> => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '0';
  iframe.style.left = '-10000px';
  iframe.style.width = '1000px';
  iframe.style.height = '8000px';
  iframe.style.border = 'none';
  // pdf=1 puts the page in export mode: it swaps the header/evaluator banner for a small
  // logo, drops the curated-plans/recommended-classes section, and renders body-area and
  // pain-scale popup content inline instead of behind a click — see personalised_plan_page.
  iframe.src = `/personalised-plan?student_uuid=${studentUuid}&pdf=1`;

  document.body.appendChild(iframe);

  try {
    const target = await waitForIframeContent(iframe);
    // Wait for the iframe's fonts to finish loading — capturing before they're ready causes
    // html2canvas to mismeasure line boxes and clip wrapped text at the top of a line.
    await iframe.contentDocument?.fonts?.ready;
    // Let images finish painting too.
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // html2canvas's `width`/`height` options directly control the rendered canvas size —
    // without them it falls back to the target's getBoundingClientRect(), which can under-
    // measure a tall, deeply-nested element. Forcing these to the true scroll size is what
    // actually prevents the bottom of a long page (e.g. the closing "Get in touch" card)
    // from being cropped out. `windowWidth`/`windowHeight` only affect the simulated layout
    // viewport used while cloning, not the crop size, so they're kept in sync but aren't
    // the fix on their own.
    // Rendered at 2x so the raster stays crisp when printed — but the PDF page itself must be
    // sized off the UNSCALED (CSS) dimensions. Feeding html2canvas's 2x pixel width straight into
    // jsPDF's page format used to produce a ~37x52 inch page (2x too big on both axes), which is
    // exactly the kind of oversized/graphic-heavy page known to trip Chrome's PDF viewer into
    // rendering unpainted (black) tile regions. Halving it back down to the element's true size
    // keeps the same crisp source image at a sane, standard-ish page size.
    const RENDER_SCALE = 2;
    const canvas = await html2canvas(target, {
      useCORS: true,
      scale: RENDER_SCALE,
      backgroundColor: '#ffffff',
      width: target.scrollWidth,
      height: target.scrollHeight,
      windowWidth: target.scrollWidth,
      windowHeight: target.scrollHeight
    });

    // A single page sized to the whole capture makes for one giant page in any PDF viewer, so
    // this slices the tall image across standard A4-ratio pages instead. A naive fixed-interval
    // slice can cut straight through the middle of a card (e.g. the teacher-note card), so page
    // breaks are snapped to the boundaries of each [data-pdf-section] block — the section is
    // pushed onto a fresh page if it wouldn't otherwise fit in the space left on the current one.
    const pageWidth = canvas.width / RENDER_SCALE;
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [pageWidth, Math.round(pageWidth * 1.4142)]
    });

    // Section/link rects and the page-break math below all stay in RASTER (canvas-pixel, i.e. 2x)
    // space, since that's the space both getBoundingClientRect() (via RENDER_SCALE) and the canvas
    // itself live in — only the final addImage/link calls convert back down to the page's own
    // (unscaled) unit space.
    const pageHeight = pdf.internal.pageSize.getHeight() * RENDER_SCALE;
    const targetRect = target.getBoundingClientRect();
    const targetTop = targetRect.top;

    // The rendered PDF page is a flat raster image — a React onClick/anchor href on the source
    // element can never fire inside a PDF viewer. A real clickable region requires a PDF link
    // annotation overlaid at the same spot, added via jsPDF's `link()` API below.
    const rectToLink = (el: HTMLElement, url: string) => {
      const rect = el.getBoundingClientRect();
      return {
        top: (rect.top - targetTop) * RENDER_SCALE,
        left: (rect.left - targetRect.left) * RENDER_SCALE,
        width: rect.width * RENDER_SCALE,
        height: rect.height * RENDER_SCALE,
        url
      };
    };

    const teacherLinks = Array.from(
      target.querySelectorAll<HTMLElement>('[data-pdf-teacher-link]')
    ).map((el) =>
      rectToLink(el, `${get_domain()}/chat?channel=${el.getAttribute('data-teacher-uuid') || ''}`)
    );

    const ctaLinks = Array.from(target.querySelectorAll<HTMLElement>('[data-pdf-cta-link]')).map(
      (el) => rectToLink(el, el.getAttribute('data-pdf-cta-url') || '')
    );

    const pdfLinks = [...teacherLinks, ...ctaLinks];

    const sections = Array.from(target.querySelectorAll<HTMLElement>('[data-pdf-section]'))
      .map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          top: (rect.top - targetTop) * RENDER_SCALE,
          bottom: (rect.bottom - targetTop) * RENDER_SCALE,
          // Always starts a fresh page, even if it would otherwise fit in the remaining space —
          // e.g. the closing "Get in touch" card, which should read like its own last page.
          forcePageBreak: el.hasAttribute('data-pdf-force-page-break')
        };
      })
      .sort((a, b) => a.top - b.top);

    // Y-coordinates where a section would otherwise straddle a page boundary, or that must
    // always start a fresh page — each one MUST be a page start.
    const forcedBreaks = [0];
    let currentPageStart = 0;
    sections.forEach((section) => {
      const sectionHeight = section.bottom - section.top;
      if (section.top < currentPageStart) return;
      // A section taller than a whole page can't be avoided — let it flow across pages as-is.
      if (sectionHeight > pageHeight && !section.forcePageBreak) return;
      if (section.forcePageBreak || section.bottom - currentPageStart > pageHeight) {
        forcedBreaks.push(section.top);
        currentPageStart = section.top;
      }
    });
    forcedBreaks.push(canvas.height);

    // Every page after the first gets a small top gap so content doesn't start flush against
    // the page edge — the first page already has HeroSection's own top spacing, so it's skipped.
    const PAGE_TOP_GAP = 40;

    // Expand into a full, non-overlapping partition of [0, canvas.height): between consecutive
    // forced breaks, fill in ordinary fixed-interval page starts (the final one in each stretch
    // ends up shorter than a full page — that's the intentional trade-off for not splitting a
    // section, and just leaves blank space at the bottom of that one page).
    const pageStarts: number[] = [];
    for (let i = 0; i < forcedBreaks.length - 1; i++) {
      let cursor = forcedBreaks[i];
      const segmentEnd = forcedBreaks[i + 1];
      while (cursor < segmentEnd) {
        const isFirstPageOverall = pageStarts.length === 0;
        pageStarts.push(cursor);
        cursor += isFirstPageOverall ? pageHeight : pageHeight - PAGE_TOP_GAP;
      }
    }

    // Each page gets its OWN cropped slice of the big canvas — reusing the same full image at
    // different offsets and relying on the page edge to "clip" it draws the same content twice,
    // once trailing off the bottom of one page and again in full at the top of the next.
    const cropSlice = (yStart: number, sliceHeight: number): string => {
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.max(1, Math.ceil(sliceHeight));
      const ctx = pageCanvas.getContext('2d') as CanvasRenderingContext2D;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, -yStart);
      return imageFormat === 'JPEG'
        ? pageCanvas.toDataURL('image/jpeg', 0.85)
        : pageCanvas.toDataURL('image/png');
    };

    pageStarts.forEach((yStart, index) => {
      const isFirstPage = index === 0;
      const topGap = isFirstPage ? 0 : PAGE_TOP_GAP;
      const yEnd = Math.min(
        index + 1 < pageStarts.length ? pageStarts[index + 1] : canvas.height,
        canvas.height
      );
      const sliceHeight = Math.min(pageHeight - topGap, yEnd - yStart);
      if (sliceHeight <= 0) return;

      if (index > 0) pdf.addPage();
      const pageImgData = cropSlice(yStart, sliceHeight);
      // Raster-space crop dimensions convert back down to the page's own (unscaled) units here —
      // the source data stays high-res, only the declared placement size shrinks to match.
      pdf.addImage(
        pageImgData,
        imageFormat,
        0,
        topGap / RENDER_SCALE,
        canvas.width / RENDER_SCALE,
        sliceHeight / RENDER_SCALE
      );

      // Overlay a real link annotation on this page for any teacher-message/CTA link that falls
      // within this page's slice — jsPDF's link() always targets whichever page was added last.
      pdfLinks
        .filter((link) => link.top >= yStart && link.top < yEnd)
        .forEach((link) => {
          pdf.link(
            link.left / RENDER_SCALE,
            (link.top - yStart + topGap) / RENDER_SCALE,
            link.width / RENDER_SCALE,
            link.height / RENDER_SCALE,
            { url: link.url }
          );
        });
    });

    return pdf;
  } finally {
    document.body.removeChild(iframe);
  }
};

export const downloadPersonalisedPlanPdf = async (
  studentUuid: string,
  studentName?: string
): Promise<void> => {
  const pdf = await buildPersonalisedPlanPdf(studentUuid, 'PNG');
  const safeName = (studentName || 'student').replace(/[^a-z0-9]+/gi, '_');
  pdf.save(`${safeName}_3_month_plan.pdf`);
};

// generatePersonalisedPlanPdfBlob produces the same PDF as a Blob (no download
// prompt) for uploading to the CDN — used by the sales-task form's Share flow.
export const generatePersonalisedPlanPdfBlob = async (
  studentUuid: string,
  studentName?: string
): Promise<{ blob: Blob; filename: string }> => {
  const pdf = await buildPersonalisedPlanPdf(studentUuid, 'JPEG');
  const safeName = (studentName || studentUuid || 'student')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return { blob: pdf.output('blob'), filename: `3-month-plan-${safeName}.pdf` };
};
